import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './AdminUsers.css';
import API_URL from '../config';

const API = API_URL;

function AdminUsers() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', first_name: '', last_name: '' });
  const [inviteConfirmed, setInviteConfirmed] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [revokeResult, setRevokeResult] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null); // { id, email, name }
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [existingUser, setExistingUser] = useState(null); // user already in app
  const [checkingEmail, setCheckingEmail] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchAdmins();
  }, [user, navigate]);

  const fetchAdmins = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/admin/app-admins`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAdmins(data);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des admins:', err);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowInviteModal(false);
    setInviteData({ email: '', first_name: '', last_name: '' });
    setInviteConfirmed(false);
    setInviteResult(null);
    setExistingUser(null);
  };

  const handleEmailBlur = async (email) => {
    if (!email || !email.includes('@')) return;
    setCheckingEmail(true);
    setExistingUser(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/admin/check-user?email=${encodeURIComponent(email)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.exists) setExistingUser(data);
      }
    } catch (err) { /* ignore */ }
    finally { setCheckingEmail(false); }
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    if (!inviteConfirmed) return;
    setInviteLoading(true);
    setInviteResult(null);
    try {
      const token = localStorage.getItem('token');
      // If user already exists in app → directly promote to admin
      const url = existingUser
        ? `${API}/api/admin/make-admin`
        : `${API}/api/admin/invite-admin`;
      const body = existingUser
        ? { email: inviteData.email }
        : inviteData;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (res.ok) {
        setInviteResult({ success: true, message: data.message });
        fetchAdmins();
        setTimeout(() => {
          closeModal();
        }, 1500);
      } else {
        setInviteResult({ success: false, message: data.detail || 'Erreur lors de l\'envoi' });
      }
    } catch (err) {
      setInviteResult({ success: false, message: 'Erreur réseau' });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevokeLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/admin/app-admins/${revokeTarget.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setRevokeResult({ success: true, message: data.message });
        fetchAdmins();
        setTimeout(() => { setRevokeTarget(null); setRevokeResult(null); }, 1500);
      } else {
        setRevokeResult({ success: false, message: data.detail || 'Erreur lors de la révocation' });
      }
    } catch (err) {
      setRevokeResult({ success: false, message: 'Erreur réseau' });
    } finally {
      setRevokeLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="dashboard">
      {/* Mobile Header */}
      <div className="mobile-header">
        <button className="hamburger-button" onClick={() => setMenuOpen(!menuOpen)}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 7L4 7" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
            <path d="M20 12L4 12" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
            <path d="M20 17L4 17" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
          </svg>
        </button>
        <img src="https://risingcard.fr/wp-content/uploads/2024/12/Logo-rising-card.svg" alt="Rising Card" className="mobile-logo" />
        <div className="mobile-spacer"></div>
      </div>

      <aside className={`sidebar ${menuOpen ? 'menu-open' : ''}`}>
        <div className="sidebar-mobile-header">
          <button className="close-menu-button" onClick={() => setMenuOpen(false)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
              <path d="M6 6L18 18" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
            </svg>
          </button>
          <img src="https://risingcard.fr/wp-content/uploads/2024/12/Logo-rising-card.svg" alt="Rising Card" className="sidebar-mobile-logo" />
          <div className="mobile-spacer"></div>
        </div>

        <Link to="/" className="sidebar-logo">
          <img src="https://risingcard.fr/wp-content/uploads/2024/12/Logo-rising-card.svg" alt="Rising Card" />
        </Link>

        <nav className="sidebar-nav">
          <Link to="/admin" className="nav-item">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
              <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
              <g id="SVGRepo_iconCarrier">
                <path d="M22 22L2 22" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
                <path d="M21 22V6C21 4.11438 21 3.17157 20.4143 2.58579C19.8285 2 18.8857 2 17 2H15C13.1144 2 12.1716 2 11.5858 2.58579C11.1143 3.05733 11.0223 3.76022 11.0044 5" stroke="#1C274C" strokeWidth="1.5"></path>
                <path d="M15 22V9C15 7.11438 15 6.17157 14.4142 5.58579C13.8284 5 12.8856 5 11 5H7C5.11438 5 4.17157 5 3.58579 5.58579C3 6.17157 3 7.11438 3 9V22" stroke="#1C274C" strokeWidth="1.5"></path>
                <path d="M9 22V19" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
                <path d="M6 8H12" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
                <path d="M6 11H12" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
                <path d="M6 14H12" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
              </g>
            </svg>
            <span>Entreprises</span>
          </Link>

          <Link to="/admin/users" className="nav-item active">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M12.0001 1.25C9.37678 1.25 7.25013 3.37665 7.25013 6C7.25013 8.62335 9.37678 10.75 12.0001 10.75C14.6235 10.75 16.7501 8.62335 16.7501 6C16.7501 3.37665 14.6235 1.25 12.0001 1.25ZM8.75013 6C8.75013 4.20507 10.2052 2.75 12.0001 2.75C13.7951 2.75 15.2501 4.20507 15.2501 6C15.2501 7.79493 13.7951 9.25 12.0001 9.25C10.2052 9.25 8.75013 7.79493 8.75013 6Z" fill="#1C274C"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M12.0001 12.25C9.68658 12.25 7.55506 12.7759 5.97558 13.6643C4.41962 14.5396 3.25013 15.8661 3.25013 17.5L3.25007 17.602C3.24894 18.7638 3.24752 20.222 4.52655 21.2635C5.15602 21.7761 6.03661 22.1406 7.22634 22.3815C8.4194 22.6229 9.97436 22.75 12.0001 22.75C14.0259 22.75 15.5809 22.6229 16.7739 22.3815C17.9637 22.1406 18.8443 21.7761 19.4737 21.2635C20.7527 20.222 20.7513 18.7638 20.7502 17.602L20.7501 17.5C20.7501 15.8661 19.5807 14.5396 18.0247 13.6643C16.4452 12.7759 14.3137 12.25 12.0001 12.25ZM4.75013 17.5C4.75013 16.6487 5.37151 15.7251 6.71098 14.9717C8.02693 14.2315 9.89541 13.75 12.0001 13.75C14.1049 13.75 15.9733 14.2315 17.2893 14.9717C18.6288 15.7251 19.2501 16.6487 19.2501 17.5C19.2501 18.8078 19.2098 19.544 18.5265 20.1004C18.156 20.4022 17.5366 20.6967 16.4763 20.9113C15.4194 21.1252 13.9744 21.25 12.0001 21.25C10.0259 21.25 8.58087 21.1252 7.52393 20.9113C6.46366 20.6967 5.84425 20.4022 5.47372 20.1004C4.79045 19.544 4.75013 18.8078 4.75013 17.5Z" fill="#1C274C"/>
            </svg>
            <span>Utilisateurs</span>
          </Link>
        </nav>

        <div className="sidebar-user">
          <div className="user-avatar">
            <img
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent((user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.email?.split('@')[0]) || 'A')}&background=fbd900&color=000&bold=true`}
              alt="Avatar"
            />
          </div>
          <div className="user-info">
            <div className="user-name">
              {user?.first_name && user?.last_name
                ? `${user.first_name} ${user.last_name}`
                : user?.email?.split('@')[0] || 'Administrateur'}
            </div>
            <div className="user-role">{user?.is_super_admin ? 'Super Admin' : 'Administrateur'}</div>
          </div>
          <button className="logout-button" onClick={logout} title="Déconnexion">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        <div className="page-header">
          <div className="page-header-left">
            <h1>Utilisateurs admin</h1>
            <p>Gérez les administrateurs de la plateforme</p>
          </div>
          {user?.is_super_admin && (
            <div className="page-header-actions">
              <button className="btn-invite-open" onClick={() => { setShowInviteModal(true); setInviteResult(null); setExistingUser(null); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <line x1="19" y1="8" x2="19" y2="14"/>
                  <line x1="22" y1="11" x2="16" y2="11"/>
                </svg>
                <span className="btn-label-full">Inviter un admin</span>
                <span className="btn-label-mobile">Inviter</span>
              </button>
            </div>
          )}
        </div>

        {revokeResult && (
          <div className={`import-result ${revokeResult.success ? 'import-success' : 'import-error'}`}>
            <span>{revokeResult.message}</span>
            <button className="import-result-close" onClick={() => setRevokeResult(null)}>×</button>
          </div>
        )}

        {revokeTarget && (
          <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !revokeLoading) { setRevokeTarget(null); setRevokeResult(null); } }}>
            <div className="modal-content">
              <div className="modal-header">
                <h2>Révoquer les droits admin</h2>
                <button className="modal-close" onClick={() => { setRevokeTarget(null); setRevokeResult(null); }} disabled={revokeLoading}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <div className="admin-invite-warning" style={{marginBottom:'1.5rem'}}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20" style={{flexShrink:0,marginTop:'0.1rem'}}>
                  <path fillRule="evenodd" clipRule="evenodd" d="M7.84308 3.80211C9.8718 2.6007 10.8862 2 12 2C13.1138 2 14.1282 2.6007 16.1569 3.80211L16.8431 4.20846C18.8718 5.40987 19.8862 6.01057 20.4431 7C21 7.98943 21 9.19084 21 11.5937V12.4063C21 14.8092 21 16.0106 20.4431 17C19.8862 17.9894 18.8718 18.5901 16.8431 19.7915L16.1569 20.1979C14.1282 21.3993 13.1138 22 12 22C10.8862 22 9.8718 21.3993 7.84308 20.1979L7.15692 19.7915C5.1282 18.5901 4.11384 17.9894 3.55692 17C3 16.0106 3 14.8092 3 12.4063V11.5937C3 9.19084 3 7.98943 3.55692 7C4.11384 6.01057 5.1282 5.40987 7.15692 4.20846L7.84308 3.80211ZM13 16C13 16.5523 12.5523 17 12 17C11.4477 17 11 16.5523 11 16C11 15.4477 11.4477 15 12 15C12.5523 15 13 15.4477 13 16ZM12 6.25C12.4142 6.25 12.75 6.58579 12.75 7V13C12.75 13.4142 12.4142 13.75 12 13.75C11.5858 13.75 11.25 13.4142 11.25 13V7C11.25 6.58579 11.5858 6.25 12 6.25Z" fill="#f97316"/>
                </svg>
                <p>Vous êtes sur le point de révoquer les droits administrateur de <strong style={{color:'#555'}}>{revokeTarget.name}</strong>. Un email de notification lui sera envoyé.</p>
              </div>
              {revokeResult && (
                <div className={`invite-modal-result ${revokeResult.success ? 'invite-success' : 'invite-error'}`} style={{marginBottom:'1rem'}}>
                  {revokeResult.message}
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => { setRevokeTarget(null); setRevokeResult(null); }} disabled={revokeLoading}>
                  Annuler
                </button>
                <button type="button" className="btn-submit" style={{background:'#fee2e2',color:'#b91c1c'}} onClick={handleRevoke} disabled={revokeLoading}>
                  {revokeLoading ? 'Traitement...' : 'Révoquer'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="admins-section">
          {loading ? (
            <div className="loading-state">Chargement...</div>
          ) : (
            <div className="admins-list">
              {admins.map((admin) => (
                <div key={admin.id} className="admin-row">
                  <div className="admin-avatar">
                    <img
                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent((admin.first_name && admin.last_name ? `${admin.first_name} ${admin.last_name}` : admin.email.split('@')[0]))}&background=fbd900&color=000&bold=true`}
                      alt="Avatar"
                    />
                  </div>
                  <div className="admin-info">
                    <div className="admin-name">
                      {admin.first_name && admin.last_name
                        ? `${admin.first_name} ${admin.last_name}`
                        : admin.email.split('@')[0]}
                    </div>
                    <div className="admin-email">{admin.email}</div>
                  </div>
                  <div className="admin-meta">
                    {admin.is_super_admin && (
                      <span className="admin-badge super-admin-badge">Super Admin</span>
                    )}
                    {admin.created_at && (
                      <span className="admin-date">Depuis le {formatDate(admin.created_at)}</span>
                    )}
                  </div>
                  <div className="admin-actions">
                    {admin.id === user?.id ? (
                      <span className="admin-self-badge">Vous</span>
                    ) : user?.is_super_admin ? (
                      <button
                        className="btn-revoke"
                        onClick={() => {
                          const name = (admin.first_name && admin.last_name)
                            ? `${admin.first_name} ${admin.last_name}`
                            : admin.email.split('@')[0];
                          setRevokeTarget({ id: admin.id, email: admin.email, name });
                          setRevokeResult(null);
                        }}
                        title="Révoquer les droits admin"
                      >
                        Révoquer
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
              {admins.length === 0 && (
                <div className="empty-state">Aucun administrateur trouvé</div>
              )}
            </div>
          )}
        </div>
      </div>

      {showInviteModal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="modal-content">
            <div className="modal-header">
              <h2>{existingUser ? 'Promouvoir administrateur' : 'Inviter un administrateur'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {existingUser && existingUser.is_admin ? (
              <div className="invite-modal-result invite-error" style={{ marginBottom: '1.5rem' }}>
                Cet utilisateur est déjà administrateur.
              </div>
            ) : (
              <>
                <div className={`admin-invite-warning ${existingUser ? 'admin-invite-warning-promote' : ''}`}>
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20" style={{flexShrink:0,marginTop:'0.1rem'}}>
                    <path fillRule="evenodd" clipRule="evenodd" d="M7.84308 3.80211C9.8718 2.6007 10.8862 2 12 2C13.1138 2 14.1282 2.6007 16.1569 3.80211L16.8431 4.20846C18.8718 5.40987 19.8862 6.01057 20.4431 7C21 7.98943 21 9.19084 21 11.5937V12.4063C21 14.8092 21 16.0106 20.4431 17C19.8862 17.9894 18.8718 18.5901 16.8431 19.7915L16.1569 20.1979C14.1282 21.3993 13.1138 22 12 22C10.8862 22 9.8718 21.3993 7.84308 20.1979L7.15692 19.7915C5.1282 18.5901 4.11384 17.9894 3.55692 17C3 16.0106 3 14.8092 3 12.4063V11.5937C3 9.19084 3 7.98943 3.55692 7C4.11384 6.01057 5.1282 5.40987 7.15692 4.20846L7.84308 3.80211ZM13 16C13 16.5523 12.5523 17 12 17C11.4477 17 11 16.5523 11 16C11 15.4477 11.4477 15 12 15C12.5523 15 13 15.4477 13 16ZM12 6.25C12.4142 6.25 12.75 6.58579 12.75 7V13C12.75 13.4142 12.4142 13.75 12 13.75C11.5858 13.75 11.25 13.4142 11.25 13V7C11.25 6.58579 11.5858 6.25 12 6.25Z" fill="#f97316"/>
                  </svg>
                  {existingUser ? (
                    <p><strong>{existingUser.first_name} {existingUser.last_name}</strong> est déjà inscrit sur la plateforme. Il sera directement promu administrateur, sans email d'invitation.</p>
                  ) : (
                    <p>Un administrateur a accès complet à toutes les entreprises, plaques NFC et données de la plateforme. Cette invitation expire dans <strong>48 heures</strong>.</p>
                  )}
                </div>

                <form onSubmit={handleInviteSubmit}>
                  {!existingUser && (
                    <div className="invite-form-row">
                      <div className="form-group">
                        <label>Prénom</label>
                        <input
                          type="text"
                          placeholder="Prénom"
                          value={inviteData.first_name}
                          onChange={(e) => setInviteData({ ...inviteData, first_name: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Nom</label>
                        <input
                          type="text"
                          placeholder="Nom"
                          value={inviteData.last_name}
                          onChange={(e) => setInviteData({ ...inviteData, last_name: e.target.value })}
                        />
                      </div>
                    </div>
                  )}
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      placeholder="email@exemple.com"
                      value={inviteData.email}
                      onChange={(e) => { setInviteData({ ...inviteData, email: e.target.value }); setExistingUser(null); }}
                      onBlur={(e) => handleEmailBlur(e.target.value)}
                      required
                    />
                    {checkingEmail && <span className="email-checking">Vérification...</span>}
                  </div>

                  <label className="confirm-checkbox">
                    <input
                      type="checkbox"
                      checked={inviteConfirmed}
                      onChange={(e) => setInviteConfirmed(e.target.checked)}
                    />
                    <span>Je confirme accorder les droits administrateur complets à cet utilisateur</span>
                  </label>

                  {inviteResult && (
                    <div className={`invite-modal-result ${inviteResult.success ? 'invite-success' : 'invite-error'}`}>
                      {inviteResult.message}
                    </div>
                  )}

                  <div className="modal-actions">
                    <button type="button" className="btn-cancel" onClick={closeModal}>
                      Annuler
                    </button>
                    <button type="submit" className="btn-submit" disabled={!inviteConfirmed || inviteLoading || !inviteData.email}>
                      {inviteLoading ? 'Envoi...' : existingUser ? 'Mettre admin' : 'Envoyer l\'invitation'}
                    </button>
                  </div>
                </form>
              </>
            )}

            {existingUser?.is_admin && (
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={closeModal}>Fermer</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
